// Simple localStorage-backed store for aggregating "Your Data" across pages
// Data model:
// {
//   profileStrength: { score, address, keywords, timestamp, ... },
//   reviews: { totalReviewCount, averageRating, responseRate30d, timestamp },
// }

const STORAGE_KEY = 'yourDataStore_v1';

const safeParse = (str) => {
  try { return JSON.parse(str); } catch { return {}; }
};

const readStore = () => {
  if (typeof window === 'undefined') return {};
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? safeParse(raw) : {};
};

const writeStore = (data) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export const saveProfileStrengthSnapshot = async (payload) => {
  const store = readStore();
  store.profileStrength = {
    ...payload,
    timestamp: Date.now(),
  };
  writeStore(store);

  // Also save to database via API
  try {
    const ApiService = (await import('../services/api')).default;
    await ApiService.post('/customer/gmb-profile-socre/save-gmb-profile-socre', {
      location: payload.locationId,
      total_score: payload.score,
      address: payload.address || '',
      keywords: JSON.stringify(payload.keywords || []),
      // Include all score breakdown if available
      ...payload.breakdown
    });
    console.log('[yourDataStore] Score saved to database successfully');
  } catch (error) {
    console.error('[yourDataStore] Failed to save score to database:', error);
    // Don't throw error - localStorage save should still work
  }
};

export const saveReviewsSnapshot = (payload) => {
  const store = readStore();
  store.reviews = {
    ...payload,
    timestamp: Date.now(),
  };
  writeStore(store);
};

export const savePerformanceSnapshot = (payload) => {
  const store = readStore();
  store.performance = {
    ...payload,
    timestamp: Date.now(),
  };
  writeStore(store);
};

export const saveBusinessProfileSnapshot = (payload) => {
  const store = readStore();
  store.businessProfile = {
    ...(store.businessProfile || {}),
    ...payload,
    timestamp: Date.now(),
  };
  writeStore(store);
};

export const saveHeatmapSnapshot = (payload) => {
  const store = readStore();

  // Initialize profile-scoped heatmap storage if it doesn't exist
  if (!store.heatmapByProfile) {
    store.heatmapByProfile = {};
  }

  // Initialize heatmap history if it doesn't exist (for backward compatibility)
  if (!store.heatmapHistory) {
    store.heatmapHistory = [];
  }

  const now = new Date();

  // Add new heatmap result to history with detailed time tracking
  const heatmapEntry = {
    ...payload,
    timestamp: Date.now(),
    date: now.toISOString(),
    // Different time format options for filtering
    daily: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    weekly: `Week ${getWeekNumber(now)} ${now.getFullYear()}`,
    monthly: now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    // Legacy month field for backward compatibility
    month: now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    // Raw date components for filtering
    year: now.getFullYear(),
    monthNumber: now.getMonth() + 1,
    weekNumber: getWeekNumber(now),
    dayOfYear: getDayOfYear(now)
  };

  // Store heatmap data scoped by profile ID (if available)
  const profileId = payload.profileId || payload.locationId;
  if (profileId) {
    // Initialize profile-specific history if needed
    if (!store.heatmapByProfile[profileId]) {
      store.heatmapByProfile[profileId] = {
        current: null,
        history: []
      };
    }

    // Save current heatmap for this profile
    store.heatmapByProfile[profileId].current = heatmapEntry;

    // Add to profile-specific history
    store.heatmapByProfile[profileId].history.unshift(heatmapEntry);

    // Keep last 30 entries per profile
    if (store.heatmapByProfile[profileId].history.length > 30) {
      store.heatmapByProfile[profileId].history = store.heatmapByProfile[profileId].history.slice(0, 30);
    }
  }

  // Keep current heatmap for backward compatibility (global)
  store.heatmap = heatmapEntry;

  // Add to global history (for backward compatibility)
  store.heatmapHistory.unshift(heatmapEntry);

  // Keep different amounts based on frequency needs
  // Daily: 30 days, Weekly: 12 weeks, Monthly: 12 months
  if (store.heatmapHistory.length > 30) {
    store.heatmapHistory = store.heatmapHistory.slice(0, 30);
  }

  writeStore(store);
};

// Get heatmap data for a specific profile
export const getHeatmapForProfile = (profileId) => {
  const store = readStore();

  if (!profileId) {
    // Fallback to global heatmap for backward compatibility
    return store.heatmap || null;
  }

  // Return profile-specific heatmap data
  return store.heatmapByProfile?.[profileId]?.current || null;
};

// Get heatmap history for a specific profile
export const getHeatmapHistoryForProfile = (profileId) => {
  const store = readStore();

  if (!profileId) {
    // Fallback to global history for backward compatibility
    return store.heatmapHistory || [];
  }

  // Return profile-specific history
  return store.heatmapByProfile?.[profileId]?.history || [];
};

// Helper function to get week number
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Helper function to get day of year
const getDayOfYear = (date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

// Fast task data retrieval - optimized for speed
export const getTasksData = (profileId = null) => {
  try {
    // Try multiple common task storage keys quickly
    const keys = [
      profileId ? `visibeen_tasks_${profileId}` : null,
      'visibeen_tasks',
      'visibeen_tasks_test_profile'
    ].filter(Boolean);

    for (const key of keys) {
      const data = localStorage.getItem(key);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        } catch (parseError) {
          // Skip invalid JSON
          continue;
        }
      }
    }

    return [];
  } catch (error) {
    return [];
  }
};

// Fast task statistics calculation with priority-based filtering
export const calculateTaskStats = (profileId = null) => {
  const allTasks = getTasksData(profileId);

  if (allTasks.length === 0) {
    return {
      totalTasks: 0,
      completedTasks: 0,
      pendingTasks: 0,
      delayedTasks: 0,
      completedPercentage: 0,
      recentTasks: []
    };
  }

  // Optimized filtering - single pass through tasks
  const incompleteCritical = [];
  const incompleteHigh = [];
  const incompleteMedium = [];
  const incompleteLow = [];
  const completedHigh = [];
  const completedMedium = [];
  const completedLow = [];

  // Single loop to categorize all tasks
  for (const task of allTasks) {
    const isCompleted = task.completed;
    const priority = task.priority;

    if (!isCompleted) {
      if (priority === 'Critical') incompleteCritical.push(task);
      else if (priority === 'High') incompleteHigh.push(task);
      else if (priority === 'Medium') incompleteMedium.push(task);
      else if (priority === 'Low') incompleteLow.push(task);
    } else {
      if (priority === 'High') completedHigh.push(task);
      else if (priority === 'Medium') completedMedium.push(task);
      else if (priority === 'Low') completedLow.push(task);
    }
  }

  // Build filtered tasks list
  let filteredTasks;
  if (incompleteCritical.length > 0) {
    filteredTasks = incompleteCritical;
  } else {
    filteredTasks = [
      ...incompleteHigh.slice(0, 2),
      ...incompleteMedium.slice(0, 1),
      ...incompleteLow.slice(0, 1)
    ];

    // Fill with completed tasks if needed (max 4 total)
    if (filteredTasks.length < 4) {
      const remaining = 4 - filteredTasks.length;
      const completedTasks = [
        ...completedHigh,
        ...completedMedium,
        ...completedLow
      ].slice(0, remaining);
      filteredTasks.push(...completedTasks);
    }
  }

  // Calculate stats
  let completedCount = 0;
  let pendingCount = 0;
  let delayedCount = 0;

  for (const task of filteredTasks) {
    if (task.completed) {
      completedCount++;
    } else {
      pendingCount++;
      if (task.priority === 'Critical') delayedCount++;
    }
  }

  // Create recent tasks (limit to 3 for performance)
  const recentTasks = filteredTasks.slice(0, 3).map((task, i) => ({
    name: task.title || task.description || `Task ${i + 1}`,
    completed: task.completed ? 1 : 0,
    total: 1,
    status: task.completed ? 'completed' : (task.priority === 'Critical' ? 'delayed' : 'pending'),
    priority: task.priority
  }));

  return {
    totalTasks: filteredTasks.length,
    completedTasks: completedCount,
    pendingTasks: pendingCount,
    delayedTasks: delayedCount,
    completedPercentage: filteredTasks.length > 0 ? Math.round((completedCount / filteredTasks.length) * 100) : 0,
    recentTasks
  };
};

// Create test tasks instantly (call this from console)
export const createTestTasks = () => {
  const testTasks = [
    { id: 'test1', title: 'Update business hours', completed: true },
    { id: 'test2', title: 'Add more photos', completed: false },
    { id: 'test3', title: 'Respond to reviews', completed: true },
    { id: 'test4', title: 'Update business description', completed: false }
  ];

  localStorage.setItem('visibeen_tasks', JSON.stringify(testTasks));

  // Force immediate page refresh to show changes
  window.location.reload();

  return testTasks;
};

// Save competitor analysis tracking data (ranking and score over time)
export const saveCompetitorTrackingSnapshot = (payload) => {
  const store = readStore();

  // Initialize competitor tracking history if it doesn't exist
  if (!store.competitorTrackingHistory) {
    store.competitorTrackingHistory = [];
  }

  const now = new Date();

  // Add new tracking entry with time tracking
  const trackingEntry = {
    rank: payload.rank || payload.myBusinessRank || 0,
    score: payload.score || payload.myBusinessScore || 0,
    keyword: payload.keyword || '',
    businessName: payload.businessName || '',
    profileId: payload.profileId || payload.locationId || null,
    locationId: payload.locationId || null,
    timestamp: Date.now(),
    date: now.toISOString(),
    daily: now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    weekly: `Week ${getWeekNumber(now)} ${now.getFullYear()}`,
    monthly: now.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    year: now.getFullYear(),
    monthNumber: now.getMonth() + 1,
    weekNumber: getWeekNumber(now),
    dayOfYear: getDayOfYear(now),
    // Grid point data for Local Pack Appearances and Geo Rank Heatmap
    greenGridPoints: payload.greenGridPoints || 0,
    yellowGridPoints: payload.yellowGridPoints || 0,
    redGridPoints: payload.redGridPoints || 0,
    grayGridPoints: payload.grayGridPoints || 0,
    totalGridPoints: payload.totalGridPoints || 0,
    averageRank: payload.averageRank || payload.rank || payload.myBusinessRank || 0,
    // Category insights from competitor analysis
    competitorInsights: payload.competitorInsights || null
  };

  // Also save competitor insights separately for quick access
  if (payload.competitorInsights) {
    const profileKey = payload.profileId || payload.locationId || 'default';
    if (!store.competitorInsightsByProfile) {
      store.competitorInsightsByProfile = {};
    }
    store.competitorInsightsByProfile[profileKey] = {
      ...payload.competitorInsights,
      timestamp: Date.now()
    };
  }

  // Add to history
  store.competitorTrackingHistory.unshift(trackingEntry);

  // Keep last 30 entries for daily tracking
  if (store.competitorTrackingHistory.length > 30) {
    store.competitorTrackingHistory = store.competitorTrackingHistory.slice(0, 30);
  }

  writeStore(store);
};

// Get competitor insights for a profile
export const getCompetitorInsights = (profileId) => {
  const store = readStore();
  if (!profileId) {
    // Return latest from history
    const latest = store.competitorTrackingHistory?.[0];
    return latest?.competitorInsights || null;
  }
  return store.competitorInsightsByProfile?.[profileId] || null;
};

// Get competitor tracking history
export const getCompetitorTrackingHistory = (profileId) => {
  const store = readStore();
  const history = store.competitorTrackingHistory || [];
  if (!profileId) {
    return history;
  }
  return history.filter(entry => entry.profileId === profileId);
};

export const getYourData = () => readStore();

export const clearYourData = () => writeStore({});
